interface NavItem {
  label: string;
  labelKey: string; // i18n key
  path: string;
  icon?: string;
  permission?: string[]; // Roles that can see this item
}

interface User {
  id?: string;
  email?: string;
  full_name?: string;
  email_verified?: boolean;
  role?: string;
  is_platform_admin?: boolean;
  is_suspended?: boolean;
}

interface NavSection {
  title: string;
  titleKey: string; // i18n key
  items: NavItem[];
}

// Central navigation configuration - MUST be used by both Navigation.tsx and Sidebar.tsx
// This ensures consistency across all navigation components
// Note: Use useNavigationSections() hook from components to get translated labels
export const navigationSectionsConfig: Omit<NavSection, 'title'>[] = [
  {
    titleKey: 'overview',
    items: [
      { labelKey: 'dashboard', label: 'Painel', path: '/dashboard', icon: 'Activity' },
      { labelKey: 'backupJobs', label: 'Backups', path: '/backup-jobs', icon: 'Database' },
      { labelKey: 'restoreJobs', label: 'Restauros', path: '/restore-jobs', icon: 'History' },
    ],
  },
  {
    titleKey: 'quickActions',
    items: [
      { labelKey: 'createBackup', label: 'Criar Backup', path: '/backups/quick', icon: 'Zap' },
    ],
  },
  {
    titleKey: 'backupRoutines',
    items: [
      {
        labelKey: 'backupConfigs',
        label: 'Planos de Backup',
        path: '/backups/configs',
        icon: 'Settings',
      },
      { labelKey: 'schedules', label: 'Agendamentos', path: '/schedules', icon: 'CalendarClock' },
    ],
  },
  {
    titleKey: 'infrastructure',
    items: [
      { labelKey: 'agents', label: 'Servidores', path: '/agents', icon: 'Monitor' },
      {
        labelKey: 'agentGroups',
        label: 'Grupos de Agentes',
        path: '/agent-groups',
        icon: 'Layers',
      },
      {
        labelKey: 'storageDestinations',
        label: 'Armazenamento',
        path: '/storage-destinations',
        icon: 'HardDrive',
      },
      {
        labelKey: 'storageBreakdown',
        label: 'Storage Breakdown',
        path: '/storage-breakdown',
        icon: 'PieChart',
      },
    ],
  },
  {
    titleKey: 'organization',
    items: [
      {
        labelKey: 'organizations',
        label: 'Organizações',
        path: '/organizations',
        icon: 'Building2',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'teamMembers',
        label: 'Membros da Equipe',
        path: '/team-members',
        icon: 'User',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'customRoles',
        label: 'Funções Personalizadas',
        path: '/custom-roles',
        icon: 'Key',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'apiKeys',
        label: 'Chaves API',
        path: '/api-keys',
        icon: 'Lock',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'apiUsage',
        label: 'Uso da API',
        path: '/api-usage',
        icon: 'TrendingUp',
        permission: ['OWNER', 'ADMIN', 'OPERATOR'],
      },
    ],
  },
  {
    titleKey: 'monitoring',
    items: [
      {
        labelKey: 'auditLogs',
        label: 'Logs de Auditoria',
        path: '/audit-logs',
        icon: 'ClipboardList',
        permission: ['OWNER', 'ADMIN', 'OPERATOR'],
      },
      {
        labelKey: 'systemLogs',
        label: 'Logs do Sistema',
        path: '/system-logs',
        icon: 'Wrench',
        permission: ['OWNER', 'ADMIN', 'OPERATOR'],
      },
      {
        labelKey: 'logRetention',
        label: 'Retenção de Logs',
        path: '/log-retention-settings',
        icon: 'Trash2',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'dataBreaches',
        label: 'Violações de Dados',
        path: '/data-breaches',
        icon: 'AlertTriangle',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'dataLocations',
        label: 'Localizações de Dados',
        path: '/data-locations',
        icon: 'MapPin',
      },
      {
        labelKey: 'complianceReports',
        label: 'Relatórios de Conformidade',
        path: '/compliance-reports',
        icon: 'FileText',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'notifications',
        label: 'Notificações',
        path: '/notifications',
        icon: 'Bell',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'webhooks',
        label: 'Webhooks',
        path: '/webhooks',
        icon: 'Link',
        permission: ['OWNER', 'ADMIN'],
      },
      { labelKey: 'metrics', label: 'Métricas', path: '/metrics', icon: 'TrendingUp' },
      {
        labelKey: 'metricsAlerts',
        label: 'Métricas e Alertas',
        path: '/metrics-alerts',
        icon: 'AlertTriangle',
        permission: ['OWNER', 'ADMIN'],
      },
      {
        labelKey: 'smtpSettings',
        label: 'Configurações SMTP',
        path: '/smtp-settings',
        icon: 'Mail',
        permission: ['OWNER', 'ADMIN'],
      },
    ],
  },
  {
    titleKey: 'billing',
    items: [
      {
        labelKey: 'billingPlans',
        label: 'Faturamento e Planos',
        path: '/billing',
        icon: 'CreditCard',
        permission: ['OWNER'],
      },
      {
        labelKey: 'invoices',
        label: 'Faturas',
        path: '/invoices',
        icon: 'Receipt',
        permission: ['OWNER', 'ADMIN'],
      },
    ],
  },
  {
    titleKey: 'admin',
    items: [
      {
        labelKey: 'adminDashboard',
        label: 'Dashboard Admin',
        path: '/admin/dashboard',
        icon: 'Library',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'analytics',
        label: 'Análises',
        path: '/admin/analytics',
        icon: 'TrendingUp',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'systemHealth',
        label: 'Saúde do Sistema',
        path: '/admin/health',
        icon: 'Activity',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'systemSettings',
        label: 'Configurações do Sistema',
        path: '/admin/settings',
        icon: 'Settings',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'integrations',
        label: 'Integrações',
        path: '/admin/integrations',
        icon: 'Plug',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'customers',
        label: 'Clientes',
        path: '/admin/customers',
        icon: 'Users',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'reports',
        label: 'Relatórios',
        path: '/admin/reports',
        icon: 'FileText',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'agentBinaries',
        label: 'Binários do Agent',
        path: '/admin/binaries',
        icon: 'Package',
        permission: ['PLATFORM_ADMIN'],
      },
      {
        labelKey: 'planManagement',
        label: 'Gerenciamento de Planos',
        path: '/admin/plans',
        icon: 'CreditCard',
        permission: ['PLATFORM_ADMIN'],
      },
    ],
  },
  {
    titleKey: 'account',
    items: [
      { labelKey: 'profile', label: 'Perfil', path: '/profile', icon: 'User' },
      {
        labelKey: 'changePassword',
        label: 'Alterar Senha',
        path: '/change-password',
        icon: 'ShieldCheck',
      },
      {
        labelKey: 'settings',
        label: 'Configurações',
        path: '/settings',
        icon: 'Settings',
        permission: ['OWNER', 'ADMIN'],
      },
    ],
  },
];

// Legacy export for backward compatibility - returns untranslated labels
// Use useNavigationSections() hook from components instead
export const navigationSections: NavSection[] = navigationSectionsConfig.map((section) => ({
  ...section,
  title: section.titleKey,
}));

// Helper function to get translated navigation sections (for use with useTranslation hook)
export const getTranslatedNavigationSections = (t: (key: string) => string): NavSection[] => {
  return navigationSectionsConfig.map((section) => ({
    ...section,
    title: t(`nav.${section.titleKey}`),
    items: section.items.map((item) => ({
      ...item,
      label: t(`nav.${item.labelKey}`),
    })),
  }));
};

// Helper function to check if user has permission for a nav item
export const hasPermission = (item: NavItem, user?: User): boolean => {
  // If no permission required, show to everyone
  if (!item.permission || item.permission.length === 0) {
    return true;
  }

  // Check if user has platform admin (highest privilege)
  if (user?.is_platform_admin) {
    return true;
  }

  // If user has role, check if it's in the allowed list
  if (user?.role) {
    return item.permission.includes(user.role);
  }

  // Default to hiding if no role is set and permission is required
  return false;
};

// Helper function to get the section title for a given path
export const getSectionForPath = (pathname: string): string | null => {
  for (const section of navigationSections) {
    for (const item of section.items) {
      // Exact match
      if (item.path === pathname) {
        return section.title;
      }
      // Prefix match for dynamic routes (e.g., /agents/:id matches /agents/abc)
      if (pathname.startsWith(item.path + '/')) {
        return section.title;
      }
    }
  }
  return null;
};
